import { useMoney } from '../hooks/useMoney';
import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Select,
  DataTable,
  EmptyState,
  Spinner,
  Banner,
  Modal,
  ProgressBar,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface ProfitOrder {
  order_id: number;
  order_number: string;
  order_date: string;
  revenue: number;
  currency: string;
  cogs: number;
  gross_profit: number;
  gross_margin_pct: number;
  item_count: number;
  fulfillment_status: string;
}

interface Summary {
  total_revenue: number;
  total_cogs: number;
  total_profit: number;
  order_count: number;
  avg_margin_pct: number;
}

interface OrderDetail {
  order: any;
  items: Array<{
    id: number;
    sku: string;
    title: string;
    quantity: number;
    price: number;
    line_revenue: number;
    cost_of_goods: number;
    line_profit: number;
    costing_method: string;
  }>;
  allocations: Array<{
    id: number;
    batch_number: string;
    batch_cost: number;
    quantity: number;
    unit_cost: number;
    total_cost: number;
    received_date: string;
    sku: string;
  }>;
  totals: {
    revenue: number;
    cogs: number;
    profit: number;
    margin_pct: number;
  };
}

interface CostingSettings {
  costing_method: 'FIFO' | 'FEFO' | 'LIFO' | 'AVERAGE';
  auto_allocate_batches: boolean;
  track_batch_costs: boolean;
}

export default function ProfitAnalysis() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();

  const [orders, setOrders] = useState<ProfitOrder[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [settings, setSettings] = useState<CostingSettings | null>(null);

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [minMargin, setMinMargin] = useState('');
  const [maxMargin, setMaxMargin] = useState('');

  // Order detail modal
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadSettings = useCallback(async () => {
    const result = await fetchApi<{ settings: CostingSettings }>('/costing/settings');
    if (result) {
      setSettings(result.settings);
    }
  }, [fetchApi]);

  const loadData = useCallback(async () => {
    let url = `/costing/profit?start_date=${startDate}&end_date=${endDate}`;
    if (minMargin) url += `&min_margin=${minMargin}`;
    if (maxMargin) url += `&max_margin=${maxMargin}`;

    const result = await fetchApi<{
      data: ProfitOrder[];
      summary: Summary;
    }>(url);

    if (result) {
      setOrders(result.data || []);
      setSummary(result.summary);
    }
  }, [fetchApi, startDate, endDate, minMargin, maxMargin]);

  const loadOrderDetail = useCallback(async (orderId: number) => {
    setLoadingDetail(true);
    try {
      const result = await fetchApi<OrderDetail>(`/costing/profit/${orderId}`);
      if (result) {
        setOrderDetail(result);
      }
    } finally {
      setLoadingDetail(false);
    }
  }, [fetchApi]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedOrderId) {
      loadOrderDetail(selectedOrderId);
    }
  }, [selectedOrderId, loadOrderDetail]);

  const formatCurrency = useMoney();

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getMarginBadge = (margin: number) => {
    if (margin < 0) return <Badge tone="critical">Loss</Badge>;
    if (margin < 10) return <Badge tone="warning">{formatPercent(margin)}</Badge>;
    if (margin < 30) return <Badge tone="info">{formatPercent(margin)}</Badge>;
    return <Badge tone="success">{formatPercent(margin)}</Badge>;
  };

  const periodOptions = [
    { label: 'Last 7 Days', value: '7' },
    { label: 'Last 30 Days', value: '30' },
    { label: 'Last 90 Days', value: '90' },
    { label: 'This Month', value: 'month' },
    { label: 'Last Month', value: 'lastmonth' },
    { label: 'Custom', value: 'custom' },
  ];

  const handlePeriodChange = (value: string) => {
    const now = new Date();
    if (value === '7') {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
    } else if (value === '30') {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
    } else if (value === '90') {
      const start = new Date(now);
      start.setDate(start.getDate() - 90);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
    } else if (value === 'month') {
      setStartDate(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
    } else if (value === 'lastmonth') {
      setStartDate(new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0]);
      setEndDate(new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0]);
    }
  };

  const marginOptions = [
    { label: 'All Margins', value: '' },
    { label: 'Loss (< 0%)', value: 'loss' },
    { label: 'Low (< 20%)', value: 'low' },
    { label: 'Medium (20-40%)', value: 'medium' },
    { label: 'High (> 40%)', value: 'high' },
  ];

  const handleMarginFilter = (value: string) => {
    if (value === 'loss') {
      setMinMargin('');
      setMaxMargin('0');
    } else if (value === 'low') {
      setMinMargin('0');
      setMaxMargin('20');
    } else if (value === 'medium') {
      setMinMargin('20');
      setMaxMargin('40');
    } else if (value === 'high') {
      setMinMargin('40');
      setMaxMargin('');
    } else {
      setMinMargin('');
      setMaxMargin('');
    }
  };

  const rows = orders.map((o) => [
    <Button key={o.order_id} variant="plain" onClick={() => setSelectedOrderId(o.order_id)}>
      {o.order_number}
    </Button>,
    o.order_date,
    formatCurrency(o.revenue, o.currency),
    formatCurrency(o.cogs, o.currency),
    formatCurrency(o.gross_profit, o.currency),
    getMarginBadge(o.gross_margin_pct),
    o.item_count.toString(),
    o.fulfillment_status === 'fulfilled' ? (
      <Badge tone="success">Fulfilled</Badge>
    ) : (
      <Badge>{o.fulfillment_status || 'Pending'}</Badge>
    ),
  ]);

  return (
    <Page
      title="Profit Analysis"
      subtitle="Analyze order profitability with FIFO/FEFO batch costing"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
      secondaryActions={[
        {
          content: 'Costing Settings',
          onAction: () => navigate('/settings'),
        },
      ]}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {settings && (
          <Layout.Section>
            <Card>
              <InlineStack gap="400" align="space-between">
                <InlineStack gap="200">
                  <Text as="span" variant="bodyMd">Costing Method:</Text>
                  <Badge tone="info">{settings.costing_method}</Badge>
                </InlineStack>
                <InlineStack gap="200">
                  <Text as="span" variant="bodyMd">Batch Tracking:</Text>
                  <Badge tone={settings.track_batch_costs ? 'success' : 'warning'}>
                    {settings.track_batch_costs ? 'Enabled' : 'Disabled'}
                  </Badge>
                </InlineStack>
                <InlineStack gap="200">
                  <Text as="span" variant="bodyMd">Auto-Allocate:</Text>
                  <Badge tone={settings.auto_allocate_batches ? 'success' : 'warning'}>
                    {settings.auto_allocate_batches ? 'Enabled' : 'Disabled'}
                  </Badge>
                </InlineStack>
              </InlineStack>
            </Card>
          </Layout.Section>
        )}

        {/* Summary Cards */}
        {summary && (
          <Layout.Section>
            <InlineStack gap="400" wrap={false}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Revenue</Text>
                  <Text variant="heading2xl" as="p">{formatCurrency(summary.total_revenue)}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{summary.order_count} orders</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Cost of Goods</Text>
                  <Text variant="heading2xl" as="p">{formatCurrency(summary.total_cogs)}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Batch costs</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Gross Profit</Text>
                  <Text variant="heading2xl" as="p">{formatCurrency(summary.total_profit)}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Revenue - COGS</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Avg Margin</Text>
                  <Text variant="heading2xl" as="p">{formatPercent(summary.avg_margin_pct)}</Text>
                  <ProgressBar
                    progress={Math.min(Math.max(summary.avg_margin_pct, 0), 100)}
                    tone={summary.avg_margin_pct >= 30 ? 'success' : 'primary'}
                    size="small"
                  />
                </BlockStack>
              </Card>
            </InlineStack>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="start">
              <div style={{ width: 150 }}>
                <Select
                  label="Period"
                  options={periodOptions}
                  value="custom"
                  onChange={handlePeriodChange}
                />
              </div>
              <div style={{ width: 150 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc', width: '100%' }}
                />
              </div>
              <div style={{ width: 150 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc', width: '100%' }}
                />
              </div>
              <div style={{ width: 150 }}>
                <Select
                  label="Margin Filter"
                  options={marginOptions}
                  value=""
                  onChange={handleMarginFilter}
                />
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Orders Table */}
        <Layout.Section>
          <Card>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            ) : orders.length === 0 ? (
              <EmptyState
                heading="No profit data"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>No orders with cost data found for this period. Ensure batch costs are being tracked.</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'numeric', 'text']}
                headings={['Order', 'Date', 'Revenue', 'COGS', 'Profit', 'Margin', 'Items', 'Status']}
                rows={rows}
                footerContent={`${orders.length} orders`}
              />
            )}
          </Card>
        </Layout.Section>

        {/* Info Section */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h2">About Profit Analysis</Text>
              <Text as="p" variant="bodyMd">
                Profit is calculated using the exact cost of each batch that was allocated to the order.
                With FIFO (First In, First Out), older batches are allocated first, giving you accurate
                cost of goods sold based on when you purchased the stock.
              </Text>
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Example</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  If you bought Batch A for €10 and Batch B for €12, and you sell 5 units:
                  With FIFO, all 5 units come from Batch A at €10 = €50 COGS.
                  With Average Cost, it would be €11 × 5 = €55 COGS.
                  The difference directly affects your reported profit margin.
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Order Detail Modal */}
      <Modal
        open={selectedOrderId !== null}
        onClose={() => {
          setSelectedOrderId(null);
          setOrderDetail(null);
        }}
        title={`Order Profit Details - ${orderDetail?.order?.order_number || ''}`}
        size="large"
      >
        <Modal.Section>
          {loadingDetail ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <Spinner size="large" />
            </div>
          ) : orderDetail ? (
            <BlockStack gap="400">
              {/* Summary */}
              <InlineStack gap="400">
                <Card>
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm" tone="subdued">Revenue</Text>
                    <Text as="span" variant="headingLg">{formatCurrency(orderDetail.totals.revenue)}</Text>
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm" tone="subdued">COGS</Text>
                    <Text as="span" variant="headingLg">{formatCurrency(orderDetail.totals.cogs)}</Text>
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm" tone="subdued">Profit</Text>
                    <Text as="span" variant="headingLg">{formatCurrency(orderDetail.totals.profit)}</Text>
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm" tone="subdued">Margin</Text>
                    <Text as="span" variant="headingLg">{formatPercent(orderDetail.totals.margin_pct)}</Text>
                  </BlockStack>
                </Card>
              </InlineStack>

              {/* Line Items */}
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Line Items</Text>
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                  headings={['SKU', 'Product', 'Qty', 'Price', 'Revenue', 'COGS', 'Profit']}
                  rows={orderDetail.items.map((item) => [
                    item.sku,
                    item.title.substring(0, 30),
                    item.quantity.toString(),
                    formatCurrency(item.price),
                    formatCurrency(item.line_revenue),
                    formatCurrency(item.cost_of_goods || 0),
                    formatCurrency(item.line_profit),
                  ])}
                />
              </BlockStack>

              {/* Batch Allocations */}
              {orderDetail.allocations.length > 0 && (
                <BlockStack gap="200">
                  <Text variant="headingSm" as="h3">Batch Allocations</Text>
                  <DataTable
                    columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'text']}
                    headings={['SKU', 'Batch', 'Qty', 'Unit Cost', 'Total Cost', 'Received']}
                    rows={orderDetail.allocations.map((a) => [
                      a.sku,
                      a.batch_number || '-',
                      a.quantity.toString(),
                      formatCurrency(a.unit_cost || a.batch_cost || 0),
                      formatCurrency(a.total_cost || 0),
                      a.received_date || '-',
                    ])}
                  />
                </BlockStack>
              )}
            </BlockStack>
          ) : null}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
